import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Database, Eye, MapPin, Smartphone, MessageSquare, Clock, Lock, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
const PrivacyPolicy = () => {
  const {
    language
  } = useLanguage();
  const translations = {
    en: {
      title: 'Privacy Policy & Data Collection',
      subtitle: 'Transparency in how we collect and use your data',
      backToSite: 'Back to Site',
      intro: "At officialtrungnguyen.com, we believe in transparency and privacy. This page explains exactly what data we collect, why we collect it, and how it's used. We DO NOT track your IP address or precise location.",
      sections: [{
        icon: <Eye className="w-6 h-6" data-id="element-1487" />,
        title: 'Personal Information',
        items: ['Name (if you choose to provide it)', 'Role or job title (optional)', 'Company or organization name (optional)', 'Email address (if provided through contact forms)']
      }, {
        icon: <MapPin className="w-6 h-6" data-id="element-1488" />,
        title: 'Location Data (Privacy-Friendly)',
        items: ['Approximate region based on timezone (e.g., "Eastern US", "Southeast Asia")', 'Country (approximate, based on timezone)', 'Timezone (e.g., America/New_York, Asia/Ho_Chi_Minh)', 'NO IP address tracking', 'NO precise city or coordinates', 'NO ISP information']
      }, {
        icon: <Smartphone className="w-6 h-6" data-id="element-1489" />,
        title: 'Device & Browser Information',
        items: ['Browser type and version (Chrome, Firefox, Safari, etc.)', 'Operating system (Windows, macOS, iOS, Android, Linux)', 'Device type (Desktop, Mobile, Tablet)', 'Screen resolution and viewport size', 'Language preference', 'Browser fingerprint (for identifying returning visitors, not tracking individuals)']
      }, {
        icon: <Database className="w-6 h-6" data-id="element-1490" />,
        title: 'Behavioral Data',
        items: ['Pages you visit and time spent on each page', 'Sections you view and scroll depth', 'Clicks, hovers, and navigation patterns', 'Entry and exit points', 'Referral source (how you found this site)', 'UTM campaign parameters (if applicable)']
      }, {
        icon: <MessageSquare className="w-6 h-6" data-id="element-1491" />,
        title: 'AI Chatbot Interactions',
        items: ['Complete transcripts of all conversations', 'Questions you ask and responses provided', 'Typing duration and message edit history', 'Sentiment analysis of your messages', 'Topic classification and intent detection', 'Files or images you upload (if any)']
      }, {
        icon: <Clock className="w-6 h-6" data-id="element-1492" />,
        title: 'Session Data',
        items: ['Session duration and active time', 'Idle time and session start/end times', 'Number of visits (returning visitor tracking)', 'Total time spent across all visits']
      }],
      whyWeCollect: 'Why We Collect This Data',
      purposes: ['To understand visitor engagement and improve user experience', 'To analyze which content resonates with different audiences', 'To provide personalized AI assistant responses', 'To detect and prevent abuse, spam, or malicious activity', 'To enable follow-up with interested visitors (recruiters, investors, etc.)', 'For research purposes to improve the website and services'],
      privacyFirst: 'Privacy-First Approach',
      privacyPoints: ['We DO NOT track your IP address', 'We DO NOT collect precise location data (city, coordinates)', 'We DO NOT share data with third-party advertisers', 'We use browser fingerprinting only to identify returning visitors, not to track individuals across the web', 'Location is approximated from timezone only (e.g., "Eastern US" instead of "New York City")', 'All data is stored locally and only shared with the site owner'],
      howWeProtect: 'How We Protect Your Data',
      protections: ['All data is encrypted and stored securely', 'Access is restricted to the site owner only via password-protected portal', 'We never sell or share your data with third parties', 'Browser fingerprints are hashed for additional security', 'Data is retained only as long as necessary for legitimate purposes'],
      yourRights: 'Your Rights',
      rights: ['You can revoke consent at any time by clearing your browser data', 'You can request to view all data we have collected about you', 'You can request deletion of your data', 'You can opt out of chatbot usage', 'You can contact us to exercise any of these rights'],
      contact: 'Contact',
      contactText: 'If you have questions about this privacy policy or want to exercise your rights, please contact:',
      email: 'contact@officialtrungnguyen.com',
      lastUpdated: 'Last Updated',
      date: 'December 2024'
    },
    vi: {
      title: 'Chính Sách Bảo Mật & Thu Thập Dữ Liệu',
      subtitle: 'Minh bạch về cách chúng tôi thu thập và sử dụng dữ liệu của bạn',
      backToSite: 'Quay Lại Trang Web',
      intro: 'Tại officialtrungnguyen.com, chúng tôi tin tưởng vào sự minh bạch và quyền riêng tư. Trang này giải thích chính xác dữ liệu nào chúng tôi thu thập, tại sao thu thập và cách sử dụng. Chúng tôi KHÔNG theo dõi địa chỉ IP hoặc vị trí chính xác của bạn.',
      sections: [{
        icon: <Eye className="w-6 h-6" data-id="element-1493" />,
        title: 'Thông Tin Cá Nhân',
        items: ['Tên (nếu bạn chọn cung cấp)', 'Vai trò hoặc chức danh công việc (tùy chọn)', 'Tên công ty hoặc tổ chức (tùy chọn)', 'Địa chỉ email (nếu cung cấp qua biểu mẫu liên hệ)']
      }, {
        icon: <MapPin className="w-6 h-6" data-id="element-1494" />,
        title: 'Dữ Liệu Vị Trí (Bảo Vệ Quyền Riêng Tư)',
        items: ['Khu vực gần đúng dựa trên múi giờ (ví dụ: "Đông Hoa Kỳ", "Đông Nam Á")', 'Quốc gia (gần đúng, dựa trên múi giờ)', 'Múi giờ (ví dụ: America/New_York, Asia/Ho_Chi_Minh)', 'KHÔNG theo dõi địa chỉ IP', 'KHÔNG theo dõi thành phố hoặc tọa độ chính xác', 'KHÔNG thu thập thông tin ISP']
      }, {
        icon: <Smartphone className="w-6 h-6" data-id="element-1495" />,
        title: 'Thông Tin Thiết Bị & Trình Duyệt',
        items: ['Loại và phiên bản trình duyệt (Chrome, Firefox, Safari, v.v.)', 'Hệ điều hành (Windows, macOS, iOS, Android, Linux)', 'Loại thiết bị (Máy tính, Di động, Máy tính bảng)', 'Độ phân giải màn hình và kích thước khung nhìn', 'Tùy chọn ngôn ngữ', 'Dấu vân tay trình duyệt (để nhận diện khách truy cập quay lại, không theo dõi cá nhân)']
      }, {
        icon: <Database className="w-6 h-6" data-id="element-1496" />,
        title: 'Dữ Liệu Hành Vi',
        items: ['Các trang bạn truy cập và thời gian dành cho mỗi trang', 'Các phần bạn xem và độ sâu cuộn', 'Nhấp chuột, di chuột và mẫu điều hướng', 'Điểm vào và điểm thoát', 'Nguồn giới thiệu (cách bạn tìm thấy trang web này)', 'Tham số chiến dịch UTM (nếu có)']
      }, {
        icon: <MessageSquare className="w-6 h-6" data-id="element-1497" />,
        title: 'Tương Tác Chatbot AI',
        items: ['Bản ghi đầy đủ của tất cả các cuộc trò chuyện', 'Câu hỏi bạn đặt và câu trả lời được cung cấp', 'Thời gian gõ và lịch sử chỉnh sửa tin nhắn', 'Phân tích cảm xúc của tin nhắn của bạn', 'Phân loại chủ đề và phát hiện ý định', 'Tệp hoặc hình ảnh bạn tải lên (nếu có)']
      }, {
        icon: <Clock className="w-6 h-6" data-id="element-1498" />,
        title: 'Dữ Liệu Phiên',
        items: ['Thời lượng phiên và thời gian hoạt động', 'Thời gian nhàn rỗi và thời gian bắt đầu/kết thúc phiên', 'Số lần truy cập (theo dõi khách truy cập quay lại)', 'Tổng thời gian dành ra cho tất cả các lần truy cập']
      }],
      whyWeCollect: 'Tại Sao Chúng Tôi Thu Thập Dữ Liệu Này',
      purposes: ['Để hiểu mức độ tương tác của khách truy cập và cải thiện trải nghiệm người dùng', 'Để phân tích nội dung nào phù hợp với các đối tượng khác nhau', 'Để cung cấp phản hồi trợ lý AI được cá nhân hóa', 'Để phát hiện và ngăn chặn lạm dụng, spam hoặc hoạt động độc hại', 'Để cho phép theo dõi với khách truy cập quan tâm (nhà tuyển dụng, nhà đầu tư, v.v.)', 'Cho mục đích nghiên cứu để cải thiện trang web và dịch vụ'],
      privacyFirst: 'Ưu Tiên Quyền Riêng Tư',
      privacyPoints: ['Chúng tôi KHÔNG theo dõi địa chỉ IP của bạn', 'Chúng tôi KHÔNG thu thập dữ liệu vị trí chính xác (thành phố, tọa độ)', 'Chúng tôi KHÔNG chia sẻ dữ liệu với các nhà quảng cáo bên thứ ba', 'Chúng tôi chỉ sử dụng dấu vân tay trình duyệt để nhận diện khách truy cập quay lại, không theo dõi cá nhân trên web', 'Vị trí được ước tính chỉ từ múi giờ (ví dụ: "Đông Hoa Kỳ" thay vì "Thành phố New York")', 'Tất cả dữ liệu được lưu trữ cục bộ và chỉ chia sẻ với chủ sở hữu trang web'],
      howWeProtect: 'Cách Chúng Tôi Bảo Vệ Dữ Liệu Của Bạn',
      protections: ['Tất cả dữ liệu được mã hóa và lưu trữ an toàn', 'Quyền truy cập chỉ dành cho chủ sở hữu trang web qua cổng được bảo vệ bằng mật khẩu', 'Chúng tôi không bao giờ bán hoặc chia sẻ dữ liệu của bạn với bên thứ ba', 'Dấu vân tay trình duyệt được băm để bảo mật bổ sung', 'Dữ liệu chỉ được giữ lại miễn là cần thiết cho mục đích hợp pháp'],
      yourRights: 'Quyền Của Bạn',
      rights: ['Bạn có thể thu hồi sự đồng ý bất cứ lúc nào bằng cách xóa dữ liệu trình duyệt', 'Bạn có thể yêu cầu xem tất cả dữ liệu chúng tôi đã thu thập về bạn', 'Bạn có thể yêu cầu xóa dữ liệu của mình', 'Bạn có thể chọn không sử dụng chatbot', 'Bạn có thể liên hệ với chúng tôi để thực hiện bất kỳ quyền nào trong số này'],
      contact: 'Liên Hệ',
      contactText: 'Nếu bạn có câu hỏi về chính sách bảo mật này hoặc muốn thực hiện quyền của mình, vui lòng liên hệ:',
      email: 'contact@officialtrungnguyen.com',
      lastUpdated: 'Cập Nhật Lần Cuối',
      date: 'Tháng 12 năm 2024'
    }
  };
  const t = translations[language];
  return <div className="min-h-screen bg-gray-50 dark:bg-slate-900" data-id="element-1499">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-12" data-id="element-1500">
        <div className="max-w-4xl mx-auto px-6" data-id="element-1501">
          <Link to="/" className="inline-flex items-center gap-2 text-blue-100 hover:text-white mb-6 transition-colors" data-id="element-1502">
            <ArrowLeft size={20} data-id="element-1503" />
            {t.backToSite}
          </Link>
          <div className="flex items-center gap-4 mb-4" data-id="element-1504">
            <div className="bg-white/20 p-3 rounded-xl" data-id="element-1505">
              <Shield className="w-10 h-10" data-id="element-1506" />
            </div>
            <div data-id="element-1507">
              <h1 className="text-4xl font-bold" data-id="element-1508">{t.title}</h1>
              <p className="text-blue-100 mt-2" data-id="element-1509">{t.subtitle}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12" data-id="element-1510">
        {/* Introduction */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg mb-8 border border-gray-200 dark:border-gray-700" data-id="element-1511">
          <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-lg" data-id="element-1512">
            {t.intro}
          </p>
        </div>

        {/* Data Collection Sections */}
        <div className="space-y-6 mb-12" data-id="element-1513">
          {t.sections.map((section, idx) => <div key={idx} className="bg-white dark:bg-slate-800 rounded-2xl p-6 shadow-lg border border-gray-200 dark:border-gray-700" data-id="element-1514">
              <div className="flex items-center gap-3 mb-4" data-id="element-1515">
                <div className="text-blue-600 dark:text-blue-400" data-id="element-1516">
                  {section.icon}
                </div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1517">
                  {section.title}
                </h2>
              </div>
              <ul className="space-y-2" data-id="element-1518">
                {section.items.map((item, itemIdx) => <li key={itemIdx} className="flex items-start gap-3 text-gray-700 dark:text-gray-300" data-id="element-1519">
                    <span className="text-blue-600 dark:text-blue-400 mt-1" data-id="element-1520">
                      •
                    </span>
                    <span data-id="element-1521">{item}</span>
                  </li>)}
              </ul>
            </div>)}
        </div>

        {/* Privacy First */}
        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-2xl p-8 shadow-lg mb-8 border border-purple-200 dark:border-purple-800" data-id="element-1522">
          <div className="flex items-center gap-3 mb-4" data-id="element-1523">
            <Lock className="w-6 h-6 text-purple-600 dark:text-purple-400" data-id="element-1524" />
            <h2 className="text-2xl font-bold text-purple-900 dark:text-purple-100" data-id="element-1525">
              {t.privacyFirst}
            </h2>
          </div>
          <ul className="space-y-3" data-id="element-1526">
            {t.privacyPoints.map((point, idx) => <li key={idx} className="flex items-start gap-3 text-gray-700 dark:text-gray-300" data-id="element-1527">
                <span className="text-purple-600 dark:text-purple-400 mt-1" data-id="element-1528">
                  🔒
                </span>
                <span data-id="element-1529">{point}</span>
              </li>)}
          </ul>
        </div>

        {/* Why We Collect */}
        <div className="bg-green-50 dark:bg-green-900/20 rounded-2xl p-8 shadow-lg mb-8 border border-green-200 dark:border-green-800" data-id="element-1530">
          <h2 className="text-2xl font-bold text-green-900 dark:text-green-100 mb-4" data-id="element-1531">
            {t.whyWeCollect}
          </h2>
          <ul className="space-y-3" data-id="element-1532">
            {t.purposes.map((purpose, idx) => <li key={idx} className="flex items-start gap-3 text-gray-700 dark:text-gray-300" data-id="element-1533">
                <span className="text-green-600 dark:text-green-400 mt-1" data-id="element-1534">
                  ✓
                </span>
                <span data-id="element-1535">{purpose}</span>
              </li>)}
          </ul>
        </div>

        {/* How We Protect */}
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-2xl p-8 shadow-lg mb-8 border border-blue-200 dark:border-blue-800" data-id="element-1536">
          <h2 className="text-2xl font-bold text-blue-900 dark:text-blue-100 mb-4" data-id="element-1537">
            {t.howWeProtect}
          </h2>
          <ul className="space-y-3" data-id="element-1538">
            {t.protections.map((protection, idx) => <li key={idx} className="flex items-start gap-3 text-gray-700 dark:text-gray-300" data-id="element-1539">
                <span className="text-blue-600 dark:text-blue-400 mt-1" data-id="element-1540">
                  🛡️
                </span>
                <span data-id="element-1541">{protection}</span>
              </li>)}
          </ul>
        </div>

        {/* Your Rights */}
        <div className="bg-orange-50 dark:bg-orange-900/20 rounded-2xl p-8 shadow-lg mb-8 border border-orange-200 dark:border-orange-800" data-id="element-1542">
          <h2 className="text-2xl font-bold text-orange-900 dark:text-orange-100 mb-4" data-id="element-1543">
            {t.yourRights}
          </h2>
          <ul className="space-y-3" data-id="element-1544">
            {t.rights.map((right, idx) => <li key={idx} className="flex items-start gap-3 text-gray-700 dark:text-gray-300" data-id="element-1545">
                <span className="text-orange-600 dark:text-orange-400 mt-1" data-id="element-1546">
                  →
                </span>
                <span data-id="element-1547">{right}</span>
              </li>)}
          </ul>
        </div>

        {/* Contact */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg mb-8 border border-gray-200 dark:border-gray-700" data-id="element-1548">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-1549">
            {t.contact}
          </h2>
          <p className="text-gray-700 dark:text-gray-300 mb-4" data-id="element-1550">
            {t.contactText}
          </p>
          <a href={`mailto:${t.email}`} className="text-blue-600 dark:text-blue-400 hover:underline font-medium text-lg" data-id="element-1551">
            {t.email}
          </a>
        </div>

        {/* Last Updated */}
        <div className="text-center text-gray-500 dark:text-gray-400 text-sm" data-id="element-1552">
          {t.lastUpdated}: {t.date}
        </div>
      </div>
    </div>;
};
export default PrivacyPolicy;